import { AiOutlineClockCircle, AiOutlineFire } from 'react-icons/ai';
import { BiPause } from 'react-icons/bi';
import { BsExclamationCircle, BsHourglass } from 'react-icons/bs';
import { MdOutlinePaid } from 'react-icons/md';

import { Draft, PayloadAction } from '@reduxjs/toolkit';
import formatters from 'formatters';
import IIntervention, {
  EInterventionStatus,
  IInterventionStyles,
} from 'models/Intervention';
import validators from 'validators';

import IGetAllInterventionResponse from 'services/httpClient/responses/intervention/IGetAllInterventionResponse';

import { IInterventionData } from '../../initialState';

export type IGetAllInterventionsSuccessWithDataAction =
  IGetAllInterventionResponse;

function getAllSuccessWithData(
  draft: Draft<IInterventionData>,
  action: PayloadAction<IGetAllInterventionsSuccessWithDataAction>,
) {
  draft.getAll.config.errorMsg = '';
  draft.getAll.config.emptyMsg = '';
  draft.getAll.config.isLoading = false;
  draft.getAll.config.count.complaint_count = action.payload.complaint_count;
  draft.getAll.config.count.date_inspection_required_count =
    action.payload.date_inspection_required_count;
  draft.getAll.config.count.date_repair_intervention_required_count =
    action.payload.date_repair_intervention_required_count;
  draft.getAll.config.count.expertise_count = action.payload.expertise_count;
  draft.getAll.config.count.expertise_date_required_count =
    action.payload.expertise_date_required_count;
  draft.getAll.config.count.info_client_needed_count =
    action.payload.info_client_needed_count;
  draft.getAll.config.count.info_insurer_needed_count =
    action.payload.info_insurer_needed_count;
  draft.getAll.config.count.info_pro_needed_count =
    action.payload.info_pro_needed_count;
  draft.getAll.config.count.inspection_planned_count =
    action.payload.inspection_planned_count;
  draft.getAll.config.count.leak_detection_date_required_count =
    action.payload.leak_detection_date_required_count;
  draft.getAll.config.count.leak_detection_planned_count =
    action.payload.leak_detection_planned_count;
  draft.getAll.config.count.no_cost_count = action.payload.no_cost_count;
  draft.getAll.config.count.paid_count = action.payload.paid_count;
  draft.getAll.config.count.process_invoice_count =
    action.payload.process_invoice_count;
  draft.getAll.config.count.process_leak_detection_report_count =
    action.payload.process_leak_detection_report_count;
  draft.getAll.config.count.quality_issue_count =
    action.payload.quality_issue_count;
  draft.getAll.config.count.quote_evaluation_count =
    action.payload.quote_evaluation_count;
  draft.getAll.config.count.repair_intervention_planned_count =
    action.payload.repair_intervention_planned_count;
  draft.getAll.config.count.trashed_count = action.payload.trashed_count;
  draft.getAll.config.count.waiting_for_invoice_count =
    action.payload.waiting_for_invoice_count;
  draft.getAll.config.count.waiting_for_leak_detection_report_count =
    action.payload.waiting_for_leak_detection_report_count;
  draft.getAll.config.count.waiting_for_quote_count =
    action.payload.waiting_for_quote_count;
  draft.getAll.config.count.urgent_repair_intervention_planned_count =
    action.payload.urgent_repair_intervention_planned_count;
  draft.getAll.config.count.cost_repair_intervention_planned_count =
    action.payload.cost_repair_intervention_planned_count;
  draft.getAll.config.count.quote_repair_intervention_planned_count =
    action.payload.quote_repair_intervention_planned_count;
  draft.getAll.config.count.urgent_date_repair_intervention_required_count =
    action.payload.urgent_date_repair_intervention_required_count;
  draft.getAll.config.count.cost_plus_date_repair_intervention_required_count =
    action.payload.cost_plus_date_repair_intervention_required_count;
  draft.getAll.config.count.quote_date_repair_intervention_required_count =
    action.payload.quote_date_repair_intervention_required_count;
  draft.getAll.config.count.new_messages = action.payload.new_messages;
  draft.getAll.config.pagination.totalPages = action.payload.needed_pages;
  draft.getAll.list = action.payload.interventions.map(intervention => {
    let formattedInterventionStyles: IInterventionStyles = {};
    switch (intervention.status) {
      case EInterventionStatus.Complaint:
      case EInterventionStatus.QualityIssue:
        formattedInterventionStyles = {
          color: 'primary.base',
          backgroundColor: 'primary.baseBg',
          icon: BsExclamationCircle,
        };
        break;

      case EInterventionStatus.QuoteEvaluation:
      case EInterventionStatus.ProcessInvoice:
      case EInterventionStatus.PaymentInProgress:
      case EInterventionStatus.ProcessLeakDetectionReport:
        formattedInterventionStyles = {
          color: 'secondary',
          backgroundColor: 'lightBlueBg',
          icon: BsHourglass,
        };
        break;

      case EInterventionStatus.InfoInsurerNeeded:
      case EInterventionStatus.InfoClientNeeded:
      case EInterventionStatus.InfoProNeeded:
      case EInterventionStatus.Expertise:
        formattedInterventionStyles = {
          color: 'gray.lightest',
          backgroundColor: 'gray.lightestBg',
          icon: BiPause,
        };
        break;

      case EInterventionStatus.Paid:
        formattedInterventionStyles = {
          color: 'basicGreen',
          backgroundColor: 'basicGreenBg',
          icon: MdOutlinePaid,
        };
        break;

      default:
        formattedInterventionStyles = {
          color: validators.isLate(intervention.repair_date)
            ? 'primary.base'
            : 'lightBlue',
          backgroundColor: validators.isLate(intervention.repair_date)
            ? 'primary.baseBg'
            : 'lightBlueBg',
          icon: validators.isLate(intervention.repair_date)
            ? AiOutlineClockCircle
            : AiOutlineFire,
        };
        break;
    }

    const interventionFormatted: IIntervention = {
      id: intervention.intervention_id,
      category_id: intervention.category_id,
      claim_id: intervention.claim_id,
      status: intervention.status,
      handler: intervention.handler,
      client_address: intervention.client_address,
      late: validators.isLate(intervention.repair_date),
      intervention_type: intervention.intervention_type,
      to_dos: intervention.to_dos,
      repair_date: formatters.date(intervention.repair_date),
      firstVisitDate: formatters.date(intervention.first_visit_date),
      name: intervention.name,
      created_at: formatters.date(intervention.created_at),
      type_of_coverage: intervention.type_of_coverage,
      styles: formattedInterventionStyles,
      insurer: intervention.insurer,
    };
    return interventionFormatted;
  });
}

export default getAllSuccessWithData;
